import _endpoints from '../../../../_endpoints'
import { useDelete } from '../../../../_hooks/useDelete'
import { useFetch } from '../../../../_hooks/useFetch'
import { usePost } from '../../../../_hooks/usePost'
import { toast } from 'react-toastify'

interface DataProduct {
  id: string
  name: string
  description: string
  tier: 'Business Optimised' | 'Curated' | 'Standard'
  accessStatus?: string
}

// The download endpoint's Content-Type header isn't reliable across databox scopes, so derive
// it from the extension instead for the types the preview modal renders inline.
const getMimeTypeFromFileName = (fileName?: string): string | undefined => {
  const name = fileName?.toLowerCase()
  if (name?.endsWith('.pdf')) return 'application/pdf'
  if (name?.endsWith('.txt')) return 'text/plain'
  if (name?.endsWith('.csv')) return 'text/csv'
  if (name?.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  }
  if (name?.endsWith('.xls')) return 'application/vnd.ms-excel'
  return undefined
}

export const useDataboxService = () => {
  const [isFetchingDataboxDetails, fetchDataboxDetails] = useFetch()
  const [isDeletingBulk, deleteData] = usePost()
  const [isDeletingBulkFiles, bulkDeleteFiles] = useDelete()
  const [isLoadingDataProducts, fetchDataProducts] = useFetch()
  const [isLoadingDataProduct, fetchDataProduct] = useFetch()
  const [isSubmitting, postData] = usePost()
  const [isCreatingDatabox, createDatabox] = usePost()
  const [isGettingDatabox, getDatabox] = useFetch()
  const [isDeletingDatabox, deleteTeamDatabox] = useDelete('invdesktop')
  const [isGettingDataboxList, getTeamDataboxes] = useFetch()
  const [isGettingDataboxes, getMyDataboxesData] = useFetch()
  const [isGettingDocuments, getDocuments] = useFetch()
  const [isUploadingDocuments, uploadDocumentsToDatabox] = usePost()
  const [isUploadingPersonalDocuments, uploadPersonalDocumentsToDatabox] = usePost('nexus')
  const [isGettingCategory, documentCategory] = useFetch()
  const [isGettingIssuer, documentIssuer] = useFetch()
  const [isGettingTags, documentTags] = useFetch()
  const [isDownloadingDocument, downloadDocument] = useFetch()
  const [isGettingDocumentsPaginated, getDocumentsPaginated] = useFetch()
  const [isGettingDataProductsByDatabox, getDataProductsByDatabox] = useFetch()
  const [isReprocessFiles, reprocessFiles] = usePost()
  const [, fetchExtractionData] = useFetch()
  const [isGettingDocumentdetails, getDocDetails] = usePost()
  const [isDeletingDataProduct, removeDataProductFromDatabox] = useDelete('invdesktop')
  const [isRemovingPersonalDataboxResources, removePersonalDataboxResources] = usePost()
  const [isDownloadingPersonalDocuments, downloadPersonalDocumentsBulk] = usePost()

  const getDataboxes = async (teamId: string) => {
    return await getTeamDataboxes(_endpoints.teams.getTeamDataboxes(teamId))
  }

  const createNewDatabox = async (databox: any) => {
    try {
      const response = await createDatabox({
        url: _endpoints.databox.createDatabox,
        data: {
          ...(databox.databoxId && { id: databox.databoxId }),
          name: databox.databoxName,
          ...(databox.databoxDescription && { description: databox.databoxDescription }),
          scope: databox.scope || 'Team',
          scopeId: databox.teamId,
          ...(databox.databoxOwner && { databoxOwner: databox.databoxOwner })
        },
        ...(databox.databoxId && { method: 'put' })
      })
      return response
    } catch (error) {
      console.error('Error creating databox:', error)
      throw error
    }
  }

  const getDataboxInfo = async (databoxId: string, isSmartDocId: boolean = false) => {
    try {
      const endpoint = _endpoints.databox.getDataboxDetails(databoxId, isSmartDocId)
      const response = await getDatabox(endpoint, {})
      return response
    } catch (error) {
      console.error('Error fetching databox info:', error)
      throw error
    }
  }

  const getDataboxDetails = async (databoxId: string, options?: any) => {
    try {
      const endpoint = _endpoints.databox.getDataboxDetails(databoxId)
      const response = await fetchDataboxDetails(endpoint, { ...options })
      return response
    } catch (error) {
      console.error('Error fetching databox details:', error)
      throw error
    }
  }

  const bulkRemoveDataProducts = async (databoxId: string, dataProductExternalIds: string[]) => {
    try {
      const response = await deleteData({
        url: _endpoints.databox.bulkRemoveDataProductsFromDatabox(databoxId),
        data: { dataProductExternalIds },
        showMessage: true,
        isAIRequest: false,
        type: 'json'
      })
      return response
    } catch (error) {
      console.error('Error deleting data products from databox:', error)
      throw error
    }
  }

  const bulkRemoveFiles = async (databoxId: string, fileIds: string[]) => {
    try {
      const response = await bulkDeleteFiles({
        url: _endpoints.databox.bulkRemoveFilesFromDatabox(databoxId),
        body: { smartDocsDocumentIds: fileIds }
      })
      const res = await response?.json()
      return res
    } catch (error) {
      console.error('Error deleting files from databox:', error)
      throw error
    }
  }

  const bulkReprocessFiles = async (fileIds: string[]) => {
    try {
      const response = await reprocessFiles({
        url: _endpoints.databox.reprocessDocuments,
        data: { documentIds: fileIds, isAutoTaggingEnabled: false },
        method: 'post'
      })
      return response
    } catch (error) {
      console.error('Error reprocessing files from databox:', error)
      throw error
    }
  }

  const fetchAvailableDataProducts = async (
    skipCount: number,
    maxResultCount: number
  ): Promise<DataProduct[]> => {
    try {
      const endpoint = _endpoints.databox.getDataProducts(skipCount, maxResultCount)
      const response = await fetchDataProducts(endpoint, {})

      if (response && response.items) {
        // Map API response to DataProduct interface
        return response.items.map((item: any) => ({
          id: item.id || item.productId || String(item.id),
          name: item.name || item.productName || 'Untitled',
          description: item.description || 'No description available',
          tier: item.tier || 'Standard',
          accessStatus: item.accessStatus,
          metadataSource: item.metadataSource,
          securityClassification: item.securityClassification,
          owners: item.owners || [],
          dataCategory: item.dataCategory || '',
          dataProviders: item.dataProviders || '',
          exploreUrl: item.exploreUrl || ''
        }))
      } else if (Array.isArray(response)) {
        // Handle case where response is directly an array
        return response.map((item: any) => ({
          id: item.id || item.productId || String(item.id),
          name: item.name || item.productName || 'Untitled',
          description: item.description || 'No description available',
          tier: item.tier || 'Standard',
          accessStatus: item.accessStatus,
          metadataSource: item.metadataSource,
          securityClassification: item.securityClassification,
          owners: item.owners || [],
          dataCategory: item.dataCategory || '',
          dataProviders: item.dataProviders || '',
          exploreUrl: item.exploreUrl || ''
        }))
      }

      return []
    } catch (error) {
      console.error('Error fetching data products:', error)
      return []
    }
  }

  const addDataProductsToDatabox = async (
    databoxId: string,
    dataProductExternalIds: string[]
  ): Promise<void> => {
    const bulkEndpoint = _endpoints.databox.addDataProductsToDataboxBulk(databoxId)

    await postData({
      url: bulkEndpoint,
      data: { dataProductExternalIds },
      hasFiles: false,
      showMessage: true,
      isAIRequest: false,
      isStreamRequest: false,
      type: 'json'
    })
  }

  const addDataProductToDatabox = async (databoxId: string, dataProductExternalId: string) => {
    try {
      const response = await postData({
        url: _endpoints.databox.addDataProductsToDatabox(databoxId),
        data: { dataProductExternalId },
        hasFiles: false,
        showMessage: false,
        isAIRequest: false,
        isStreamRequest: false,
        type: 'json'
      })
      return response
    } catch (error) {
      console.error('Error adding data product to databox:', error)
      throw error
    }
  }

  const deleteDatabox = async (databoxId: string) => {
    return await deleteTeamDatabox({
      url: _endpoints.databox.deleteDatabox(databoxId)
    })
  }

  const getMyDataboxes = async (skipCount = 0, maxResultCount = 100) => {
    return await getMyDataboxesData(_endpoints.nexus.getDataboxes(skipCount, maxResultCount))
  }

  const getDocumentBlobUrl = async (documentId: string, fileName: string) => {
    // Directly fetch file data from the API
    const fileData = await downloadDocument(_endpoints.databox.downloadDocument(documentId), {
      isBlob: true
    })

    if (fileData && !(fileData instanceof Error)) {
      // Use the response content type if it's a Blob, or fall back to a generic binary type
      const mimeType =
        fileData instanceof Blob
          ? fileData.type || 'application/octet-stream'
          : 'application/octet-stream'
      const blob = new Blob([fileData], { type: mimeType })
      const objectURL = URL.createObjectURL(blob)

      // Create download link and trigger download
      const a = document.createElement('a')
      a.href = objectURL
      a.download = fileName || 'document'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      // Delay cleanup so the browser has time to initiate the download
      setTimeout(() => URL.revokeObjectURL(objectURL), 1000)
    } else {
      toast.error('Failed to download the file. Please try again.')
    }
  }

  // Fetches the file's content and hands back an in-memory blob URL + mime type for previewing,
  // instead of triggering a download. Caller is responsible for revoking the object URL
  // (via URL.revokeObjectURL) once the preview is closed.
  const getDocumentPreviewData = async (documentId: string, fileName?: string) => {
    const fileData = await downloadDocument(_endpoints.databox.downloadDocument(documentId), {
      isBlob: true
    })

    if (fileData && !(fileData instanceof Error)) {
      // Prefer the mime type implied by the file's extension (reliable across both Team and
      // Personal databox scopes) and only fall back to whatever the response itself reports.
      const mimeType =
        getMimeTypeFromFileName(fileName) ||
        (fileData instanceof Blob
          ? fileData.type || 'application/octet-stream'
          : 'application/octet-stream')
      const blob = new Blob([fileData], { type: mimeType })
      const objectURL = URL.createObjectURL(blob)
      return { url: objectURL, mimeType }
    }

    toast.error('Failed to load the file preview. Please try again.')
    return null
  }

  const getDataboxDocuments = async (databoxId: string) => {
    const response = await getDocuments(_endpoints.databox.databoxDocuments(databoxId))
    // Filter out documents with null or empty externalId
    if (Array.isArray(response)) {
      return response.filter((doc: any) => doc.externalId !== null && doc.externalId !== '')
    }
    return response
  }

  const getDataboxDocumentsPaginated = async (
    databoxId: string | number,
    pageIndex = 1,
    pageSize = 10,
    searchCriteria?: string | undefined,
    sortBy?: string | undefined,
    sortDirection?: 'asc' | 'desc' | undefined,
    status?: string | undefined
  ) => {
    const params = new URLSearchParams({
      pageIndex: String(pageIndex),
      pageSize: String(pageSize),
      ...(searchCriteria && { searchCriteria }),
      ...(sortBy && { sortBy }),
      ...(sortDirection && { sortDirection }),
      ...(status && { status })
    })
    const endpoint = `${_endpoints.nexus.getDataboxDocuments(databoxId)}?${params.toString()}`
    const response = await getDocumentsPaginated(endpoint)
    // Drop only truly unusable entries (no id AND no externalId)
    if (Array.isArray(response)) {
      return response.filter(
        (doc: any) => doc.id !== null || (doc.externalId !== null && doc.externalId !== '')
      )
    }
    return response
  }

  const getDataboxDataProductsPaginated = async (
    databoxId: string | number,
    pageIndex = 1,
    pageSize = 10,
    searchCriteria?: string | undefined,
    sortBy?: string | undefined,
    sortDirection?: 'asc' | 'desc' | undefined
  ) => {
    const params = new URLSearchParams({
      pageIndex: String(pageIndex),
      pageSize: String(pageSize),
      ...(searchCriteria && { searchCriteria }),
      ...(sortBy && { sortBy }),
      ...(sortDirection && { sortDirection })
    })
    const endpoint = `${_endpoints.nexus.getDataboxDataProduct(databoxId)}?${params.toString()}`
    return await getDataProductsByDatabox(endpoint)
  }

  const getDocumentCategory = async () => {
    return await documentCategory(_endpoints.databox.getDocumentCategory)
  }

  const getDocumentIssuer = async (filter: string, teamId: string) => {
    const baseUrl = _endpoints.databox.getDocumentIssuer
    const queryParams = new URLSearchParams({
      filter,
      teamId
    })
    const fullUrl = `${baseUrl}?${queryParams.toString()}`

    return await documentIssuer(fullUrl)
  }

  const getDocumentTags = async (teamId: string) => {
    return await documentTags(_endpoints.databox.getDocumentTags(teamId))
  }

  const uploadDocuments = async (databoxId: string, files: FileList, metadata: any[]) => {
    const formData = new FormData()

    // Add files to FormData
    Array.from(files).forEach((file) => {
      formData.append('files', file)
    })

    // Add metadata as JSON string array
    formData.append('metadata', JSON.stringify(metadata))

    return await uploadDocumentsToDatabox({
      url: _endpoints.databox.databoxDocuments(databoxId),
      data: formData,
      hasFiles: true
    })
  }

  const uploadDocumentsPersonal = async (databoxId: string, files: FileList, metadata: any[]) => {
    const results = []
    const fileArray = Array.from(files)

    for (let i = 0; i < fileArray.length; i++) {
      const formData = new FormData()
      formData.append('threadId', '')
      formData.append('file', fileArray[i])
      // Databox uploads reject duplicates so the user is told a copy already exists.
      formData.append('ReuseExisting', 'false')

      const result = await uploadPersonalDocumentsToDatabox({
        url: _endpoints.nexus.uploadResource,
        data: formData,
        hasFiles: true
      })
      results.push(result)
    }

    return results
  }

  const getDataProduct = async (dataProductId: string): Promise<DataProduct | null> => {
    try {
      const endpoint = _endpoints.databox.getDataProduct(dataProductId)
      const response = await fetchDataProduct(endpoint, {})

      if (response) {
        return response
      }
      return null
    } catch (error) {
      console.error('Error fetching data product:', error)
      return null
    }
  }

  // Helper: Fetch all data sources in parallel
  const fetchAllDataSources = async (
    databoxIds: string[],
    dataProductIds: string[],
    cognitiveSearchIds: string[],
    dataProductsByDatabox: Array<{ dataProductId: string; databoxId: string }>
  ) => {
    const databoxPromises = databoxIds.map((id) =>
      getDataboxInfo(id, true).catch((err) => {
        console.error(`Error fetching databox ${id}:`, err)
        return null
      })
    )

    const dataProductsPromises = dataProductIds.map((id) =>
      getDataProduct(id).catch((err) => {
        console.error(`Error fetching data product ${id}:`, err)
        return null
      })
    )

    const databoxDataProductIds = dataProductsByDatabox.map((dp) => dp.dataProductId)
    const databoxDataProductsPromises = databoxDataProductIds.map((id) =>
      getDataProduct(id).catch((err) => {
        console.error(`Error fetching databox data product ${id}:`, err)
        return null
      })
    )

    const documentDetailsPromise =
      cognitiveSearchIds.length > 0
        ? getDocumentDetails(cognitiveSearchIds).catch((err) => {
            console.error('Error fetching document details:', err)
            return null
          })
        : Promise.resolve(null)

    return await Promise.all([
      Promise.all(databoxPromises),
      Promise.all(dataProductsPromises),
      documentDetailsPromise,
      Promise.all(databoxDataProductsPromises)
    ])
  }

  // Helper: Group documents by databox ID for efficient lookup
  // cognitiveSearchIds is a positional fallback if the API response doesn't include it directly.
  const groupDocumentsByDatabox = (documentDetails: any, cognitiveSearchIds: string[] = []) => {
    const documentsByDataboxMap = new Map<string, any[]>()

    if (documentDetails && Array.isArray(documentDetails)) {
      documentDetails.forEach((doc: any, index: number) => {
        // API responses have used both field names for this - check both
        const docDataboxId = doc?.smartDocsDataboxId || doc?.databoxId
        if (docDataboxId) {
          const databoxIdStr = String(docDataboxId)
          if (!documentsByDataboxMap.has(databoxIdStr)) {
            documentsByDataboxMap.set(databoxIdStr, [])
          }
          documentsByDataboxMap.get(databoxIdStr)!.push({
            id: doc.externalId || doc.id,
            name: doc.name,
            type: 'file',
            smartDocsDocumentId: doc.id,
            cognitiveSearchId: doc.cognitiveSearchId || cognitiveSearchIds[index]
          })
        }
      })
    }

    return documentsByDataboxMap
  }

  // Helper: Group data products by databox ID with deduplication
  const groupDataProductsByDatabox = (
    databoxDataProductResults: any[],
    dataProductsByDatabox: Array<{ dataProductId: string; databoxId: string }>
  ) => {
    const dataProductsByDataboxMap = new Map<string, any[]>()
    const seenDataProducts = new Set<string>()

    databoxDataProductResults.forEach((dp: any, index) => {
      if (dp) {
        const databoxId = String(dataProductsByDatabox[index]?.databoxId)
        const uniqueKey = `${dp.id}_${databoxId}`

        if (!seenDataProducts.has(uniqueKey)) {
          seenDataProducts.add(uniqueKey)

          if (!dataProductsByDataboxMap.has(databoxId)) {
            dataProductsByDataboxMap.set(databoxId, [])
          }
          dataProductsByDataboxMap.get(databoxId)!.push({
            id: dp.id,
            name: dp.name,
            type: 'dataproduct',
            metadataSource: dp.metadataSource,
            tier: dp.tier,
            securityClassification: dp.securityClassification
          })
        }
      }
    })

    return dataProductsByDataboxMap
  }

  // Helper: Map databox results with selected items
  const mapDataboxResults = (
    databoxResults: any[],
    documentsByDataboxMap: Map<string, any[]>,
    dataProductsByDataboxMap: Map<string, any[]>
  ) => {
    return databoxResults
      .filter((db: any): db is NonNullable<typeof db> => db !== null)
      .map((db: any) => {
        const dbIdStr = String(db.id)
        const smartDocsIdStr = String(db.smartDocsDataboxId)

        const databoxDocuments =
          documentsByDataboxMap.get(dbIdStr) || documentsByDataboxMap.get(smartDocsIdStr) || []

        const databoxDataProducts =
          dataProductsByDataboxMap.get(dbIdStr) ||
          dataProductsByDataboxMap.get(smartDocsIdStr) ||
          []

        const selectedItems = [...databoxDocuments, ...databoxDataProducts]

        return {
          id: db.id,
          smartDocsDataboxId: db.smartDocsDataboxId,
          name: db.name,
          filesCount: db.filesCount ?? 0,
          dataProductsCount: db.dataProductsCount ?? 0,
          totalSizeFormatted: db.totalSizeFormatted ?? '0 B',
          owner: db.createdBy?.displayName ?? '',
          createdAt: db.createdAt,
          teamId: db.teamId ?? db.team?.smartDocsTeamId ?? '',
          scopeId: db.scopeId ?? '',
          ...(selectedItems.length > 0 && { selectedItems })
        }
      })
  }

  const fetchDataSourceDetails = async (
    databoxIds: string[],
    dataProductIds: string[],
    cognitiveSearchIds: string[] = [],
    dataProductsByDatabox: Array<{ dataProductId: string; databoxId: string }> = []
  ): Promise<{
    databoxes: Array<{
      id: string
      smartDocsDataboxId?: string
      name: string
      filesCount: number
      dataProductsCount: number
      totalSizeFormatted: string
      owner: string
      createdAt: string
      teamId?: string
      scopeId?: string
      selectedItems?: any[]
    }>
    dataProducts: Array<{
      id: string
      name: string
      metadataSource: string
      tier: string
      securityClassification: string
    }>
  }> => {
    // Fetch all data sources in parallel
    const [databoxResults, dataProductResults, documentDetails, databoxDataProductResults] =
      await fetchAllDataSources(
        databoxIds,
        dataProductIds,
        cognitiveSearchIds,
        dataProductsByDatabox
      )

    // Group documents and data products by databox for efficient lookup
    const documentsByDataboxMap = groupDocumentsByDatabox(documentDetails, cognitiveSearchIds)
    const dataProductsByDataboxMap = groupDataProductsByDatabox(
      databoxDataProductResults,
      dataProductsByDatabox
    )

    // Map databox results with their selected items
    const databoxes = mapDataboxResults(
      databoxResults,
      documentsByDataboxMap,
      dataProductsByDataboxMap
    )

    // Map standalone data products
    const dataProducts: any[] = dataProductResults
      .filter((dp: any): dp is DataProduct => dp !== null)
      .map((dp: any) => ({
        id: dp.id,
        name: dp.name,
        metadataSource: dp.metadataSource,
        tier: dp.tier,
        securityClassification: dp.securityClassification,
        description: dp.description,
        owners: dp.owners
      }))

    return { databoxes, dataProducts }
  }

  const extractDocumentDetails = async (
    documentId: string,
    exportFormat: string,
    isGenericPdfExtraction: boolean
  ) => {
    const params = new URLSearchParams({
      modelId: 'prebuilt-layout',
      isGenericPdfExtraction: isGenericPdfExtraction?.toString(),
      ...(exportFormat && { inUrl: exportFormat }),
      ...(isGenericPdfExtraction
        ? { formatter: 'excel' }
        : { indexName: 'common', embeddingModel: 'stella-en-1.5B-v5' })
    })
    const urlWithParams = `${_endpoints.databox.bulkExtract(documentId)}?${params.toString()}`

    try {
      const res = await fetchExtractionData(urlWithParams, {
        isBlob: true
      })
      if (res instanceof Blob) {
        const text = await res.text()
        try {
          const json = JSON.parse(text)
          if (json && (json.error || json.errorCode)) {
            // API error in JSON body
            return {
              error: true,
              message: json.message || json.error || text
            }
          }
        } catch {
          // Not JSON, check for common error signatures
          if (typeof text === 'string') {
            if (text.toLowerCase().includes('error') || text.includes('Exception')) {
              return { error: true, message: text }
            }
          }
        }
        return res
      }
      return res
    } catch (err: any) {
      return { error: true, code: err?.status || 500, message: err?.message || 'Unknown error' }
    }
  }

  const getDocumentDetails = async (cognitiveSearchIds: string[]) => {
    try {
      if (!cognitiveSearchIds || cognitiveSearchIds.length === 0) {
        throw new Error('Cognitive Search IDs are required')
      }

      const response = await getDocDetails({
        url: _endpoints.databox.getDocumentDetails,
        data: { cognitiveSearchIds },
        method: 'post'
      })
      return response
    } catch (error) {
      console.error('Error fetching document details:', error)
      throw error
    }
  }

  const deleteDataProductFromDatabox = async (databoxId: string, dataProductId: string) => {
    return await removeDataProductFromDatabox({
      url: _endpoints.databox.removeDataProductFromDatabox(databoxId, dataProductId)
    })
  }

  // Bulk-removes files and/or data products from a Personal databox in a single call - a
  // single-item delete is just a one-item resourceIds/dataProductIds array.
  const bulkRemoveResourcesFromPersonalDatabox = async (
    personalDataboxId: string,
    resourceIds: string[] = [],
    dataProductIds: string[] = []
  ) => {
    return await removePersonalDataboxResources({
      url: _endpoints.databox.bulkRemoveResourcesFromPersonalDatabox(personalDataboxId),
      data: {
        resourceIds,
        deleteResources: true,
        dataProductIds
      }
    })
  }

  // Bulk-downloads files from a Personal databox as a single archive. isStreamRequest hands
  // back the raw fetch Response (instead of parsed JSON) so the binary body can be read as a
  // blob and saved to disk.
  const bulkDownloadPersonalDocuments = async (
    resourceIds: string[],
    fileName = 'documents.zip'
  ) => {
    const response = await downloadPersonalDocumentsBulk({
      url: _endpoints.databox.bulkDownloadPersonalDocuments,
      data: { resourceIds },
      isStreamRequest: true
    })

    if (!response || typeof response.blob !== 'function') {
      toast.error('Failed to download the selected files. Please try again.')
      return
    }

    const blob = await response.blob()
    const objectURL = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectURL
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(objectURL), 1000)
  }

  return {
    isFetchingDataboxDetails,
    getDataboxDetails,
    isDeletingBulk,
    bulkRemoveDataProducts,
    isLoadingDataProducts,
    fetchAvailableDataProducts,
    isSubmitting,
    addDataProductsToDatabox,
    addDataProductToDatabox,
    isCreatingDatabox,
    createNewDatabox,
    isGettingDatabox,
    getDataboxInfo,
    isDeletingDatabox,
    deleteDatabox,
    isGettingDataboxList,
    getDataboxes,
    isGettingDocuments,
    getDataboxDocuments,
    isUploadingDocuments,
    uploadDocuments,
    isUploadingPersonalDocuments,
    uploadDocumentsPersonal,
    isGettingCategory,
    getDocumentCategory,
    isGettingIssuer,
    getDocumentIssuer,
    isGettingTags,
    getDocumentTags,
    isDeletingBulkFiles,
    bulkRemoveFiles,
    isDownloadingDocument,
    getDocumentBlobUrl,
    getDocumentPreviewData,
    fetchDataSourceDetails,
    isGettingDataboxes,
    getMyDataboxes,
    isGettingDocumentsPaginated,
    getDocumentsPaginated,
    getDataboxDocumentsPaginated,
    isGettingDataProductsByDatabox,
    getDataProductsByDatabox,
    getDataboxDataProductsPaginated,
    isReprocessFiles,
    bulkReprocessFiles,
    extractDocumentDetails,
    getDataProduct,
    isLoadingDataProduct,
    isGettingDocumentdetails,
    getDocumentDetails,
    isDeletingDataProduct,
    deleteDataProductFromDatabox,
    isRemovingPersonalDataboxResources,
    bulkRemoveResourcesFromPersonalDatabox,
    isDownloadingPersonalDocuments,
    bulkDownloadPersonalDocuments
  }
}
